import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Mail, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import { CurrentPlanCard } from "@/components/billing/CurrentPlanCard";
import { BriefPreferenceCard } from "@/components/billing/BriefPreferenceCard";
import { PaymentHistory } from "@/components/billing/PaymentHistory";
import { CancelModal } from "@/components/billing/CancelModal";
import { UsageCard, FeatureCard } from "@/components/billing/UsageCards";
import { daysBetween } from "@/components/billing/format";

export default function Billing() {
  const { user, refreshProfile } = useAuth();
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [resuming, setResuming] = useState(false);
  const [briefEnabled, setBriefEnabled] = useState(true);
  const [briefSaving, setBriefSaving] = useState(false);

  const plan = user?.plan || "free";
  const feats = user?.plan_features || {};
  const usage = user?.usage || { generations: 0, lifetime_generations: 0 };
  const metering = feats.metering || "monthly";
  const limit = feats.generations_limit ?? feats.generations_per_month ?? 10;
  const used = metering === "lifetime"
    ? (usage.lifetime_generations || 0)
    : (usage.generations || 0);
  const usagePct = limit === -1 ? 0 : Math.min(100, Math.round((used / limit) * 100));
  const isPaid = plan !== "free";
  const billingEnabled = user?.billing_enabled !== false;

  useEffect(() => {
    if (!billingEnabled) return;
    (async () => {
      try {
        const { data } = await api.get("/billing/history");
        setHistory(data.items || []);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [billingEnabled]);

  useEffect(() => {
    if (user?.daily_brief_enabled !== undefined) {
      setBriefEnabled(!!user.daily_brief_enabled);
    }
  }, [user?.daily_brief_enabled]);

  // Billing hidden until Stripe goes live.
  if (user && !billingEnabled) {
    return <Navigate to="/app" replace />;
  }

  const toggleBrief = async (next) => {
    setBriefEnabled(next); // optimistic
    setBriefSaving(true);
    try {
      await api.put("/brief/preferences", { enabled: next });
      toast.success(next
        ? "Daily Brief is on. See you at 06:00 UTC."
        : "Daily Brief paused. You can turn it back on anytime.");
      await refreshProfile();
    } catch (e) {
      setBriefEnabled(!next);
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update preference.");
    } finally {
      setBriefSaving(false);
    }
  };

  const cancel = async () => {
    setCancelling(true);
    try {
      const { data } = await api.post("/billing/cancel");
      const endDate = data.plan_expires_at
        ? new Date(data.plan_expires_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })
        : "the end of your billing period";
      toast.success(`Subscription cancelled. You keep access until ${endDate}.`);
      setConfirmCancel(false);
      await refreshProfile();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCancelling(false);
    }
  };

  const resume = async () => {
    setResuming(true);
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back — your subscription will renew as scheduled.");
      await refreshProfile();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setResuming(false);
    }
  };

  return (
    <div data-testid="billing-page" className="min-h-screen">
      <header className="border-b border-gray-200 px-4 sm:px-8 py-5 sm:py-6 sticky top-14 lg:top-0 bg-white z-20">
        <div className="font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-gray-500">
          Account · Billing
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-[-0.025em] font-medium mt-2">
          Billing &amp; subscription
        </h1>
      </header>

      <div className="px-4 sm:px-8 py-6 sm:py-8 max-w-5xl space-y-5 sm:space-y-6">
        <CurrentPlanCard
          plan={plan}
          isPaid={isPaid}
          renewIn={daysBetween(user?.plan_expires_at)}
          cancelScheduled={!!user?.plan_cancel_scheduled}
          expiresAt={user?.plan_expires_at}
          onCancel={() => setConfirmCancel(true)}
          onResume={resume}
          resuming={resuming}
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <UsageCard used={used} limit={limit} pct={usagePct} metering={metering} />
          <FeatureCard
            icon={Mail}
            color="#EC4899"
            bg="#FBE2EE"
            title="Daily Brief"
            on={!!feats.daily_brief}
            offNote="Solo+"
          />
          <FeatureCard
            icon={CalendarDays}
            color="#7C3AED"
            bg="#EFE6FE"
            title="Weekly Calendar"
            on={!!feats.weekly_calendar}
            offNote="Pro+"
          />
        </div>

        {feats.daily_brief && (
          <BriefPreferenceCard
            enabled={briefEnabled}
            saving={briefSaving}
            onToggle={toggleBrief}
          />
        )}

        <PaymentHistory history={history} loading={loadingHistory} />

        <div className="text-xs font-mono uppercase tracking-widest text-gray-500 flex items-center gap-2">
          <Sparkles size={12} strokeWidth={1.5} className="text-[#7C3AED]" />
          Charges appear as <span className="font-semibold text-gray-700">FOUNT STORIES</span> on your statement
        </div>
      </div>

      {confirmCancel && (
        <CancelModal
          plan={plan}
          expiresAt={user?.plan_expires_at}
          cancelling={cancelling}
          onConfirm={cancel}
          onClose={() => setConfirmCancel(false)}
        />
      )}
    </div>
  );
}
